import React, { useState, useEffect } from 'react';
import {
  Grid,
  Typography,
  Box,
  Alert,
  CircularProgress
} from '@mui/material';
import HomeRepairServiceIcon from '@mui/icons-material/HomeRepairService';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import PlumbingIcon from '@mui/icons-material/Plumbing';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import ElevatorIcon from '@mui/icons-material/Elevator';
import ApartmentIcon from '@mui/icons-material/Apartment';
import { httpRequest } from 'src/services/httpRequest';

const feeItems: {
  name: string;
  icon: React.ElementType;
  color: 'primary' | 'warning' | 'info' | 'error' | 'secondary' | 'success';
}[] = [
  {
    name: 'Building',
    icon: ApartmentIcon,
    color: 'primary'
  },
  {
    name: 'Electrical',
    icon: FlashOnIcon,
    color: 'warning'
  },
  {
    name: 'Plumbing',
    icon: PlumbingIcon,
    color: 'info'
  },
  {
    name: 'Fire',
    icon: LocalFireDepartmentIcon,
    color: 'error'
  },
  {
    name: 'Mechanical',
    icon: HomeRepairServiceIcon,
    color: 'secondary'
  },
  {
    name: 'Elevator',
    icon: ElevatorIcon,
    color: 'success'
  }
];

const FeeSummaryPage = ({ applicationID }) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [feeData, setFeeData] = useState<Record<string, number>>({});
  const [total, setTotal] = useState<number>(0);

  const fetchFeeData = async () => {
    try {
      setLoading(true);

      // Make the API call to get fee data
      const response = await httpRequest(
        'GET',
        // `/construction/ucc_form_share_list?ApplicationID=${applicationID}`
        `/construction/get_ucc_form_fee/${applicationID}`
      );

      if (response?.status === 'success' && response.data?.summary) {
        // Process the fee data
        const newFeeData: Record<string, number> = {};

        // Filter items where subcode is DATA_FOUND and Fee > 0
        response.data.summary.forEach((item: any) => {
          if (item.subcode === 'DATA_FOUND') {
            newFeeData[item.Type] = item.Fee;
          }
        });

        setFeeData(newFeeData);
        setTotal(response.data.finalFees || 0);
      } else {
        console.error('Failed to fetch fee details');
      }
    } catch (error) {
      console.error('Error fetching fee data:', error);
      // toast.error('Failed to fetch fee details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFeeData();
  }, []);

  // Filter fee items to show only those with fees
  const filteredFeeItems = feeItems?.filter((item) => feeData[item.name] >= 0);
  
  return (
    <Box mt={5}>
      <Typography variant="h4" gutterBottom>
        Estimated Fees
      </Typography>

      <Alert severity="info" sx={{ mb: 2, mt: 2 }}>
        The fees below are estimated based on the information provided in this
        application and are subject to change upon review.
      </Alert>

      {loading ? (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container sx={{ justifyContent: 'space-between' }}>
          <Grid item xs={12} md={6} container spacing={2}>
            <Grid item xs={6}>
              <Typography fontWeight="bold">Subcode</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography fontWeight="bold">Fee</Typography>
            </Grid>

            {filteredFeeItems.length > 0 ? (
              filteredFeeItems.map((item, index) => {
                const Icon = item.icon;
                return (
                  <React.Fragment key={index}>
                    <Grid item xs={6} display="flex" alignItems="center" gap={1}>
                      <Icon color={item.color} />
                      <Typography color={item.color}>{item.name}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography>${feeData[item.name].toFixed(2)}</Typography>
                    </Grid>
                  </React.Fragment>
                );
              })
            ) : (
              <Grid item xs={12}>
                <Typography color="text.secondary" sx={{ my: 2 }}>
                  No fee items found.
                </Typography>
              </Grid>
            )}
            <Grid item xs={6}>
              <Typography fontWeight="bold">Total</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography fontWeight="bold">${total.toFixed(2)}</Typography>
            </Grid>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default FeeSummaryPage;
